import { createMesh } from './createMesh';
import { createLine } from './createLine';
import { Geometry } from './Geometry';
import { OPACITY_ACTIVE, OPACITY_INACTIVE, AXIS_REF_LINE_LENGTH } from './constants';
import { Object3D, Vector3, Euler } from 'three';
const asMeshBasicMaterial = (material) => material;
const updateOpacity = (opacity, ...meshs) => meshs.forEach(({ material }) => asMeshBasicMaterial(material).opacity = opacity);
export class Arrow extends Object3D {
    constructor(axisName, rotateAxis, editor, material, length, transform) {
        super();
        this.interactiveObjects = [];
        this._renderOrderObjects = [];
        this._toolObjects = [];
        this.axisName = axisName;
        this.rotateAxis = rotateAxis;
        this.editor = editor;
        const { name } = editor;
        const position = new Vector3(...transform.position);
        this.axis = position.clone();
        position.multiplyScalar(length);
        this.pivot = position.clone();
        const rotation = new Euler(...transform.rotation);
        const scale = new Vector3(length, length, length);
        // --------------------------------------------------------------
        // reference line
        // --------------------------------------------------------------
        const from = position.clone();
        const to = from.clone().add(this.axis);
        this._refLine = createLine(from, to, material.refLine());
        this._refLine.renderOrder = Arrow.RenderOrder.ReferenceLine;
        this._refLine.visible = false;
        this.add(this._refLine);
        // --------------------------------------------------------------
        // axis reference line
        // --------------------------------------------------------------
        this._axisRefLine = createLine(this.axis.clone().multiplyScalar(-AXIS_REF_LINE_LENGTH * 0.5), this.axis.clone().multiplyScalar(AXIS_REF_LINE_LENGTH * 0.5), material.axisRefLine());
        this._axisRefLine.visible = false;
        this.add(this._axisRefLine);
        // --------------------------------------------------------------
        // rotation axis reference line
        // --------------------------------------------------------------
        this._rotAxisRefLine = createLine(this.rotateAxis.clone().multiplyScalar(-AXIS_REF_LINE_LENGTH * 0.5), this.rotateAxis.clone().multiplyScalar(AXIS_REF_LINE_LENGTH * 0.5), material.axisRefLine());
        this._rotAxisRefLine.visible = false;
        this.add(this._rotAxisRefLine);
        // --------------------------------------------------------------
        // rotation angle start line
        // --------------------------------------------------------------
        this._rotAngleStartLine = createLine(new Vector3(0, 0, 0), this.pivot.clone().multiplyScalar(0.8), material.rotAngleStartLine());
        this._rotAngleStartLine.visible = false;
        this.add(this._rotAngleStartLine);
        // --------------------------------------------------------------
        // rotation angle line
        // --------------------------------------------------------------
        this._rotateAngle = createMesh(`${name}.${axisName}.rotate.angle`, Geometry.rotateAngleLine(), material.normal(), {
            position: position.clone().multiplyScalar(0.4),
            scale: scale.clone().multiplyScalar(0.8),
            rotation,
        });
        this._rotateAngle.visible = false;
        updateOpacity(OPACITY_ACTIVE, this._rotateAngle);
        this.add(this._rotateAngle);
        // --------------------------------------------------------------
        // axis rotation arc
        // --------------------------------------------------------------
        const axisRotateArc = createMesh(`${name}.${axisName}.axis.rotateArc`, Geometry.axisRotateArc(), material.extra(), {
            rotation,
            scale,
        });
        axisRotateArc.visible = false;
        this.add(axisRotateArc);
        this._renderOrderObjects.push(axisRotateArc);
        // --------------------------------------------------------------
        // fat arrow line
        // --------------------------------------------------------------
        this._arrowLine = createMesh(`${name}.${axisName}.arrow.line`, Geometry.arrowLine(), material.normal(), {
            position: position.clone().multiplyScalar(0.5),
            rotation,
            scale,
        });
        this.add(this._arrowLine);
        this._renderOrderObjects.push(this._arrowLine);
        this._toolObjects.push(this._arrowLine);
        // --------------------------------------------------------------
        // rotate tool
        // --------------------------------------------------------------
        const rotateTool = createMesh(`${name}.${axisName}.rotateTool`, Geometry.rotateTool(), material.normal(), {
            rotation,
            scale,
        });
        this._rotateTool = rotateTool;
        rotateTool.addEventListener('highlight', () => {
            this.highlight();
            updateOpacity(OPACITY_ACTIVE, rotateTool); // , axisRotateArc);
            axisRotateArc.visible = true;
        });
        rotateTool.addEventListener('unhighlight', () => {
            this.unhighlight();
            updateOpacity(OPACITY_INACTIVE, rotateTool); // , axisRotateArc);
            axisRotateArc.visible = false;
        });
        rotateTool.addEventListener('click', () => {
            this.editor.activateRotateTool(this.axisName);
            this._rotAxisRefLine.visible = true;
            this._rotateTool.visible = true;
            this._rotAngleStartLine.visible = true;
            this._rotateAngle.visible = true;
        });
        this.add(rotateTool);
        this._renderOrderObjects.push(rotateTool);
        this._toolObjects.push(rotateTool);
        this.interactiveObjects.push(rotateTool);
        // --------------------------------------------------------------
        // scale tool
        // --------------------------------------------------------------
        const scaleTool = createMesh(`${name}.${axisName}.scaleTool`, Geometry.scaleTool(), material.normal(), {
            position: position.clone().multiplyScalar(0.5),
            rotation,
            scale,
        });
        this._scaleTool = scaleTool;
        scaleTool.addEventListener('highlight', () => {
            this.highlight();
            updateOpacity(OPACITY_ACTIVE, scaleTool);
        });
        scaleTool.addEventListener('unhighlight', () => {
            this.unhighlight();
            updateOpacity(OPACITY_INACTIVE, scaleTool);
        });
        scaleTool.addEventListener('click', () => {
            this.editor.activateScaleTool(this.axisName);
        });
        this.add(scaleTool);
        this._renderOrderObjects.push(scaleTool);
        this._toolObjects.push(scaleTool);
        this.interactiveObjects.push(scaleTool);
        // --------------------------------------------------------------
        // translate tool
        // --------------------------------------------------------------
        const translateTool = createMesh(`${name}.${axisName}.translateTool`, Geometry.translateTool(), material.normal(), {
            position: position.clone().multiplyScalar(1.1),
            rotation,
            scale,
        });
        this._translateTool = translateTool;
        translateTool.addEventListener('highlight', () => {
            this.highlight();
            updateOpacity(OPACITY_ACTIVE, translateTool);
        });
        translateTool.addEventListener('unhighlight', () => {
            this.unhighlight();
            updateOpacity(OPACITY_INACTIVE, translateTool);
        });
        translateTool.addEventListener('click', () => {
            this.editor.activateTranslateTool(this.axisName);
            this._axisRefLine.visible = true;
        });
        this.add(translateTool);
        this._renderOrderObjects.push(translateTool);
        this._toolObjects.push(translateTool);
        this.interactiveObjects.push(translateTool);
        // --------------------------------------------------------------
        // axis plane (used by translation)
        // --------------------------------------------------------------
        // this.axisPlane = createMesh(`${name}.${axisName}.axisPlane`, Geometry.axisPlane(), material.hidden(), {
        //   position,
        //   rotation,
        //   scale,
        // });
        // this.add(this.axisPlane);
        // ---
        this._setRenderOrder(Arrow.RenderOrder.Default);
    }
    highlight() {
        updateOpacity(OPACITY_ACTIVE, this._arrowLine);
        this._setRenderOrder(Arrow.RenderOrder.Highlight);
    }
    unhighlight() {
        updateOpacity(OPACITY_INACTIVE, this._arrowLine);
        this._setRenderOrder(Arrow.RenderOrder.Default);
    }
    resetTools() {
        this._toolObjects.forEach((obj) => obj.visible = true);
        this._axisRefLine.visible = false;
        this._rotAxisRefLine.visible = false;
        this._rotAngleStartLine.visible = false;
        this._rotateAngle.visible = false;
        this.setRotationAngle(0);
    }
    hideTools() {
        this._toolObjects.forEach((obj) => obj.visible = false);
    }
    enableRefLine() {
        this._refLine.visible = true;
    }
    disableRefLine() {
        this._refLine.visible = false;
    }
    enableRotationTool(enable) {
        if (this._rotateTool) {
            this._rotateTool.visible = enable;
        }
    }
    enableTranslationTool(enable) {
        if (this._translateTool) {
            this._translateTool.visible = enable;
        }
    }
    enableScalingTool(enable) {
        if (this._scaleTool) {
            this._scaleTool.visible = enable;
        }
    }
    setRotationAngle(angle) {
        this._rotateTool.setRotationFromAxisAngle(this.rotateAxis, angle);
        this._rotateTool.updateMatrix();
        this._rotateAngle.setRotationFromAxisAngle(this.rotateAxis, angle);
        this._rotateAngle.updateMatrix();
    }
    drawRefLine(from, to) {
        const position = this._refLine.geometry.attributes.position;
        const posArr = position.array;
        posArr[0] = from.x;
        posArr[1] = from.y;
        posArr[2] = from.z;
        posArr[3] = to.x;
        posArr[4] = to.y;
        posArr[5] = to.z;
        position.needsUpdate = true;
    }
    drawRefLineTo(to) {
        const position = this._refLine.geometry.attributes.position;
        const posArr = position.array;
        posArr[3] = to.x;
        posArr[4] = to.y;
        posArr[5] = to.z;
        position.needsUpdate = true;
    }
    _setRenderOrder(renderOrder) {
        this._renderOrderObjects.forEach((obj) => obj.renderOrder = renderOrder);
    }
}
// tslint:disable-next-line:variable-name
Arrow.RenderOrder = {
    Default: 10000,
    Highlight: 10010,
    ReferenceLine: 10020,
};
//# sourceMappingURL=Arrow.js.map